import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, buildWhatsAppLink } from "@/lib/api";
import { toast } from "sonner";
import { Check, ArrowUpRight } from "lucide-react";

export default function BookingDialog({ open, onOpenChange, settings }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const wa = buildWhatsAppLink(settings?.whatsapp_number);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      toast.success("Enquiry sent! We'll reach out soon.");
    } catch (err) {
      toast.error("Could not send. Please try WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm({ name: "", phone: "", email: "", message: "" });
    setDone(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid="booking-dialog" className="sm:max-w-[520px] bg-white rounded-3xl border-earth-100 p-8">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-earth-900 font-light">
            {done ? "Thank you." : "Plan your stay"}
          </DialogTitle>
          <DialogDescription className="text-earth-600">
            {done ? "We'll be in touch shortly." : "Share a few details — or message us directly on WhatsApp."}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center gap-5 py-6">
            <div className="w-16 h-16 rounded-full bg-sage-100 flex items-center justify-center">
              <Check className="w-7 h-7 text-sage-700" strokeWidth={1.5} />
            </div>
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-earth-900 text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-earth-800 transition-colors"
            >
              Continue on WhatsApp <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4 mt-2">
            <Input data-testid="booking-name-input" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input data-testid="booking-phone-input" placeholder="Phone / WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Input data-testid="booking-email-input" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Textarea data-testid="booking-message-input" placeholder="Tell us the dates / what you have in mind" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            <div className="flex items-center justify-between gap-3 pt-2">
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-earth-700 underline underline-offset-4 hover:text-earth-900"
              >
                Prefer WhatsApp →
              </a>
              <Button
                type="submit"
                disabled={loading}
                data-testid="booking-submit-btn"
                className="bg-earth-900 text-white hover:bg-earth-800 rounded-full px-6"
              >
                {loading ? "Sending…" : "Send enquiry"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
